import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { LEGAL_VERSIONS, CONSENT_GATE_CUTOFF_DATE } from '@/constants/legal';

const REQUIRED_DOCUMENTS = ['tos', 'privacy_policy'] as const;

/**
 * Checks whether the current user has accepted the latest versions
 * of all required legal documents. Returns true if consent is complete.
 */
function useConsentStatus(userId: string | undefined) {
  return useQuery({
    queryKey: ['legal-consent-status', userId, LEGAL_VERSIONS.tos, LEGAL_VERSIONS.privacy_policy],
    queryFn: async () => {
      if (!userId) return { consented: true }; // Shouldn't happen — guard exists upstream

      const { data, error } = await supabase
        .from('legal_consents')
        .select('document_type, document_version')
        .eq('user_id', userId);

      if (error) throw error;

      // Check that each required document has a record matching the current version
      const hasAll = REQUIRED_DOCUMENTS.every(docType => {
        const version = LEGAL_VERSIONS[docType];
        return data?.some(
          row => row.document_type === docType && row.document_version === version
        );
      });

      return { consented: hasAll };
    },
    enabled: !!userId,
    staleTime: 5 * 60 * 1000, // 5 minutes — gate is mounted once in persistent layout
    retry: 2,
  });
}

/**
 * Gate component that blocks access to product features until the user
 * has accepted the current versions of ToS and Privacy Policy.
 *
 * Grandfathering: Users created before CONSENT_GATE_CUTOFF_DATE are exempt.
 * Re-consent: When LEGAL_VERSIONS is bumped, users must re-accept.
 */
export function ConsentGate({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const userId = user?.id;
  const queryClient = useQueryClient();

  // Grandfather existing users — skip gate for users created before cutoff
  const userCreatedAt = user?.created_at;
  const isGrandfathered = userCreatedAt
    ? new Date(userCreatedAt) < new Date(CONSENT_GATE_CUTOFF_DATE)
    : false;

  const { data, isLoading, isError } = useConsentStatus(
    isGrandfathered ? undefined : userId
  );

  // Grandfathered users pass through
  if (isGrandfathered) {
    return <>{children}</>;
  }

  // Loading — fail-closed (don't show app content)
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Checking account status...</p>
        </div>
      </div>
    );
  }

  // Error — allow retry
  if (isError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <CardTitle>Unable to verify account</CardTitle>
            <CardDescription>
              We couldn't verify your account status. Please try again.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              className="w-full"
              onClick={() => queryClient.invalidateQueries({ queryKey: ['legal-consent-status'] })}
            >
              Retry
            </Button>
          </CardFooter>
        </Card>
      </div>
    );
  }

  // Consent is complete — render children
  if (data?.consented) {
    return <>{children}</>;
  }

  // Consent missing — show acceptance UI
  return <ConsentAcceptanceView userId={userId!} />;
}

function ConsentAcceptanceView({ userId }: { userId: string }) {
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const queryClient = useQueryClient();

  const handleAccept = async () => {
    setSubmitting(true);
    const now = new Date().toISOString();

    const { error } = await supabase.from('legal_consents').upsert([
      { user_id: userId, document_type: 'tos', document_version: LEGAL_VERSIONS.tos, accepted_at: now },
      { user_id: userId, document_type: 'privacy_policy', document_version: LEGAL_VERSIONS.privacy_policy, accepted_at: now },
    ], { onConflict: 'user_id,document_type,document_version', ignoreDuplicates: true });

    if (error) {
      // Duplicate key (409/23505) means consent already exists — treat as success
      const isDuplicate = error.code === '23505' || error.message?.includes('duplicate key');
      if (!isDuplicate) {
        console.warn('[Legal] Failed to record consent:', error.message);
        toast.error('There was an issue recording your consent. Please try again.');
        setSubmitting(false);
        return;
      }
    }

    // Invalidate cache so the gate re-evaluates
    await queryClient.invalidateQueries({ queryKey: ['legal-consent-status'] });
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <ShieldCheck className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-2xl">Terms & Privacy</CardTitle>
          <CardDescription className="text-base">
            Before continuing, please review and accept our Terms of Service and Privacy Policy.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-lg border p-4 space-y-2 text-sm text-muted-foreground">
            <p>By accepting, you confirm that you have read and agree to:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li>
                <Link to="/terms" target="_blank" className="text-primary underline hover:text-primary/80">
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link to="/privacy" target="_blank" className="text-primary underline hover:text-primary/80">
                  Privacy Policy
                </Link>
              </li>
            </ul>
          </div>
          <div className="flex items-start space-x-2">
            <Checkbox
              id="consent-gate-terms"
              checked={accepted}
              onCheckedChange={(checked) => setAccepted(checked === true)}
              className="mt-0.5"
            />
            <label htmlFor="consent-gate-terms" className="text-sm leading-snug cursor-pointer">
              I have read and agree to the Terms of Service and Privacy Policy
            </label>
          </div>
        </CardContent>
        <CardFooter>
          <Button
            className="w-full"
            size="lg"
            disabled={!accepted || submitting}
            onClick={handleAccept}
          >
            {submitting ? 'Processing...' : 'Continue'}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
